import { Section, SectionHeading, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";
import { Compass, HeartHandshake } from "lucide-react";
import { asset } from "../lib/assets";

const allenHeadshot = { url: asset("allen-headshot.png") };
const danielHeadshot = { url: asset("daniel-headshot.png") };
const ilanHeadshot = { url: asset("hs_ilan_circle.PNG") };
const jtHeadshot = { url: asset("hs_jt_circle.PNG") };
const aqibHeadshot = { url: asset("aqib-headshot.png") };
const aliHeadshot = { url: asset("ali-zia-headshot.png") };
const rizwanHeadshot = { url: asset("rizwan-ali-headshot.png") };

const team = [
    { name: "Allen Azarkian", role: "Founder & CEO", image: allenHeadshot.url, row: 1 },
    { name: "Daniel Lee", role: "Product Manager", image: danielHeadshot.url, row: 1 },
    { name: "Ilan Danial", role: "Developer", image: ilanHeadshot.url, row: 1 },
    { name: "JT Smith", role: "Product Manager", image: jtHeadshot.url, row: 1 },
    { name: "Aqib Javaid", role: "Developer", image: aqibHeadshot.url, row: 2 },
    { name: "Ali Zia", role: "Developer", image: aliHeadshot.url, row: 2 },
    { name: "Rizwan Ali", role: "Developer", image: rizwanHeadshot.url, row: 2 },
];
const values = [
        { icon: Compass, title: "How we work", body: "We treat public data as infrastructure — ingested continuously, normalized carefully, and delivered fast enough to act on." },
    { icon: HeartHandshake, title: "What we promise", body: "No hidden data lags, no surprise fees, and a human who knows the agency process when you need one." },
];
const mission = {
    lead: "No property owner should learn about a violation from a penalty letter.",
    body: "We watch every NYC agency record attached to your buildings, tell you what changed when Violerts detects a new or updated record, and stay with you until it is closed.",
};
function About() {
    return (<>
      <PageHero title="We built Violerts because we kept getting the bad news last." description="Violerts is a compliance monitoring platform that watches every agency record attached to your properties and tells you what changed, what it means, and how to resolve it."/>

      <Section>
        <SectionHeading title="Built from two decades inside NYC real estate."/>
        <div className="mt-8 grid gap-6 text-base leading-relaxed text-muted-foreground md:grid-cols-2">
          <p>
            Violerts was founded in 2014 by Allen Azarkian, an architect and real-estate operator who saw the same
            problem from every seat he had held — property manager, broker, landlord, and builder.
            With an undergraduate degree in Finance, a Master's in Architecture, and more than 23 years
            in the industry, Allen understood that compliance failures rarely start with bad intent;
            they start with fragmented data, missed notices, and the gaps between agency systems.
          </p>
          <p>
            That insight became Violerts: an all-in-one property compliance monitoring platform that
            brings every NYC agency record into one place. Allen built a team of professionals from
            across the real-estate world, secured more than a dozen licenses, and forged direct vendor
            relationships to remove intermediaries, cut delays, and keep pricing competitive. The
            result is a streamlined, seamless way for property owners and managers to stay ahead of
            violations before they become expensive problems.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {values.map((v) => (<div key={v.title} className="rounded-xl border border-border bg-card p-7 shadow-card">
              <v.icon className="h-5 w-5 text-foreground"/>
              <h3 className="mt-5 text-lg font-semibold">{v.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
            </div>))}
        </div>
      </Section>

      <Section className="surface-ink">
        <SectionHeading align="center" onInk title="The people behind the platform."/>
        {[1, 2].map((row) => (<div key={row} className={"mx-auto flex max-w-[1240px] flex-wrap justify-center gap-6 " + (row === 1 ? "mt-12" : "mt-6")}>
            {team
                .filter((m) => m.row === row)
                .map((m) => (<div key={m.name} className="flex w-[calc(50%-0.75rem)] flex-col items-center rounded-xl border border-ink-foreground/10 bg-surface/60 px-6 py-5 text-center sm:w-[232px]">
                  {m.image
                    ? <img src={m.image} alt={`${m.name} headshot`} className="h-28 w-28 rounded-full border-2 border-signal object-cover"/>
                    : <div className="flex h-28 w-28 items-center justify-center rounded-full border-2 border-signal bg-surface text-2xl font-semibold text-signal">{m.name.split(" ").map((n) => n[0]).join("")}</div>}
                  <h3 className="mt-3 text-[15px] font-semibold leading-tight text-ink-foreground">{m.name}</h3>
                  <p className="mt-1 text-xs text-signal">{m.role}</p>
                </div>))}
          </div>))}
      </Section>

      <Section>
        <SectionHeading align="center" title={mission.lead} description={mission.body}/>
      </Section>
      <PageCta />
    </>);
}

export default About;
